import * as Sentry from '@sentry/react';
import { createFileRoute, useParams, useRouter, useSearch } from '@tanstack/react-router';
import { useGetBenchmarkQuery } from '@vizit/api';
import { BenchmarkImageSortOption, parseBenchmarkImageSortOption } from '@vizit/collections';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import { type Heatmap, isHeatmap } from '@vizit/organisms';
import type { BreadcrumbItem } from '@vizit/utils';
import * as React from 'react';
import { resolveLoaderWorkspaceId, useActiveWorkspaceId } from '../../../../../hooks/use-workspace-link';
import { BenchmarkDetailsPage, PagesProvider } from '../../../../../pages';
import { beforeLoadAuthenticatedValidation, buildBenchmarkBreadcrumbs } from '../../../../../router/route-utils';
import { guardCustomBenchmarks, withBenchmarkName } from '../../../../../router/route-utils';
import { BreadcrumbsPageTemplate } from '../../../../../templates';

export const Route = createFileRoute('/_authenticated/w/$workspaceSlug/library/benchmarks/$benchmarkId')({
  staticData: {
    pageName: 'Benchmark Details',
  },
  beforeLoad: async ({ context, location, params }) => {
    await beforeLoadAuthenticatedValidation(context, location);
    await guardCustomBenchmarks(context, params.workspaceSlug);
  },
  component: RouteComponent,
  validateSearch: (
    search: Record<string, unknown>,
  ): { sort?: BenchmarkImageSortOption; heatmap?: Exclude<Heatmap, 'original'> } => {
    const sort = parseBenchmarkImageSortOption(search.sort);
    // Newest is the default, so it stays out of the URL.
    return {
      sort: sort === BenchmarkImageSortOption.Newest ? undefined : sort,
      heatmap: isHeatmap(search.heatmap) && search.heatmap !== 'original' ? search.heatmap : undefined,
    };
  },
  loader: async ({ location, params, context }) => {
    const workspaceId = resolveLoaderWorkspaceId(context, params.workspaceSlug);

    // A direct load runs this before the access context carries the workspace, so the name stays unresolved here and
    // the page renames the crumb once it has the benchmark (see useBenchmarkNameBreadcrumbs).
    let benchmarkName = 'Benchmark';

    if (workspaceId) {
      try {
        const variables = { workspace_id: workspaceId, benchmark_id: params.benchmarkId };
        const benchmark = await context.queryClient.ensureQueryData({
          queryKey: useGetBenchmarkQuery.getKey(variables),
          queryFn: useGetBenchmarkQuery.fetcher(variables),
        });
        benchmarkName = benchmark.name || benchmarkName;
      } catch (error) {
        Sentry.captureException(error, {
          level: 'warning',
          tags: {
            feature: SENTRY_FEATURE_TAGS.Navigation,
            stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Navigation].breadcrumbs,
          },
          extra: {
            page: 'benchmark details',
          },
        });
      }
    }

    const breadcrumbs = buildBenchmarkBreadcrumbs(
      location.state?.from,
      params.workspaceSlug,
      benchmarkName,
      location.search,
    );

    return { breadcrumbs };
  },
});

/**
 * On a direct load the loader runs before the access context carries the workspace, so it cannot fetch the benchmark
 * and labels the current crumb "Benchmark". The page resolves the benchmark right after through this same query, so
 * following it here renames the crumb once the name is in; `undefined` keeps the loader's crumbs as they are.
 */
function useBenchmarkNameBreadcrumbs(benchmarkId: string, workspaceId?: string): BreadcrumbItem[] | undefined {
  const { breadcrumbs } = Route.useLoaderData();
  const { data: benchmark } = useGetBenchmarkQuery(
    { workspace_id: workspaceId ?? '', benchmark_id: benchmarkId },
    { enabled: !!workspaceId && !!benchmarkId },
  );

  return React.useMemo(() => withBenchmarkName(breadcrumbs, benchmark?.name), [breadcrumbs, benchmark?.name]);
}

function RouteComponent() {
  const { benchmarkId } = useParams({ from: Route.id });
  const router = useRouter();
  const search = useSearch({ from: Route.id });

  const workspaceId = useActiveWorkspaceId() ?? undefined;
  const breadcrumbs = useBenchmarkNameBreadcrumbs(benchmarkId, workspaceId);

  const handleSortOptionChange = (option: BenchmarkImageSortOption) => {
    router.navigate({
      to: Route.fullPath,
      params: { benchmarkId },
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        sort: option === BenchmarkImageSortOption.Newest ? undefined : option,
      }),
    });
  };

  const handleHeatmapChange = (nextHeatmap: Heatmap) => {
    router.navigate({
      to: Route.fullPath,
      params: { benchmarkId },
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        heatmap: nextHeatmap === 'original' ? undefined : nextHeatmap,
      }),
    });
  };

  return (
    <PagesProvider template={BreadcrumbsPageTemplate} templateProps={{ breadcrumbs }}>
      <BenchmarkDetailsPage
        benchmarkId={benchmarkId}
        workspaceId={workspaceId}
        sortOption={search.sort ?? BenchmarkImageSortOption.Newest}
        onSortOptionChange={handleSortOptionChange}
        heatmap={search.heatmap ?? 'original'}
        onHeatmapChange={handleHeatmapChange}
      />
    </PagesProvider>
  );
}
